export type Language = 'en' | 'pl' | 'sk'

export const languageNames: Record<Language, string> = {
  en: 'English',
  pl: 'Polski',
  sk: 'Slovensky',
}

type TranslationKeys = typeof en

const en = {
  // Common
  loading: 'Loading...',
  cancel: 'Cancel',
  create: 'Create',
  save: 'Save',
  delete: 'Delete',
  send: 'Send',
  dismiss: 'Dismiss',
  you: 'You',
  ai: 'AI',
  msgs: 'msgs',
  messages: 'messages',
  threads: 'threads',
  folders: 'folders',
  yes: 'Yes',
  no: 'No',
  back: 'Back',
  example: 'Example',
  vs: 'vs',

  // Nav
  navDashboard: 'Dashboard',
  navSettings: 'Settings',
  navProjects: 'Projects',
  navSignOut: 'Sign out',
  navHideSidebar: 'Hide sidebar',
  navShowSidebar: 'Show sidebar',
  navHidePanel: 'Hide panel',
  navShowPanel: 'Show panel',

  // Landing - Hero
  landingSignIn: 'Sign In',
  landingGetStarted: 'Get Started',
  landingBadge: 'Multi-AI Workspace',
  landingTitle1: 'Every AI model.',
  landingTitle2: 'One workspace to rule them all.',
  landingSubtitle: 'Synapsios is the central hub for working with multiple AI models. Organize projects, chat with different models, and never lose context. Use your own keys, lease from others, or go premium.',
  landingStartFree: 'Start for Free',
  landingHaveAccount: 'I have an account',

  // Landing - Nav links
  landingNavFeatures: 'Features',
  landingNavPricing: 'Pricing',
  landingNavHow: 'How it works',

  // Landing - Window mock
  landingMockTitle: 'Synapsios — AI Workspace',
  landingMockProject: 'My Project',
  landingMockArch: 'Architecture',
  landingMockFrontend: 'Frontend Code',
  landingMockResearch: 'AI Research',
  landingMockMsg1: 'How should I design the auth system?',
  landingMockReply1: 'I recommend a token-based approach with JWT...',
  landingMockMsg2: 'What does GPT-4 think?',
  landingMockReply2: 'Consider session-based auth for better security...',

  // Landing - Features
  featuresTitle: 'Everything you need',
  featuresSubtitle: 'One platform, unlimited possibilities',
  featureMultiAiTitle: 'Multi-AI in One Place',
  featureMultiAiDesc: 'OpenAI, Anthropic, xAI, DeepSeek — every major AI model accessible from a single interface. Switch between models mid-conversation.',
  featureProjectsTitle: 'Projects & Folders',
  featureProjectsDesc: 'Organize threads into projects and folders with unlimited nesting. Each project is its own workspace.',
  featureThreadsTitle: 'Smart Threads',
  featureThreadsDesc: 'Every message remembers which model generated it. Full conversation history with context sent to AI automatically.',
  featureFastTitle: 'Lightning Fast',
  featureFastDesc: 'Local MySQL database, zero external dependencies. Your data stays on your server. No cold starts, no latency.',
  featureApiTitle: 'Flexible API Access',
  featureApiDesc: 'Use your own API keys, lease access from other users at micro-rates, or subscribe to our premium managed keys.',
  featureDecisionsTitle: 'Decisions & Summaries',
  featureDecisionsDesc: 'Save key findings, generate AI-powered thread summaries. Never lose an important decision again.',

  // Landing - Pricing
  pricingTitle: 'Simple, flexible pricing',
  pricingSubtitle: 'Own your keys, lease from the community, or let us handle everything.\nPay only for what makes sense for you.',

  pricingFreeName: 'Free',
  pricingFreePrice: '$0',
  pricingFreePeriod: '',
  pricingFreeDesc: 'Bring your own API keys',
  pricingFreeFeat1: 'Unlimited projects & threads',
  pricingFreeFeat2: 'All AI models supported',
  pricingFreeFeat3: 'Use your own API keys',
  pricingFreeFeat4: 'Full conversation history',
  pricingFreeFeat5: 'Local data storage',
  pricingFreeFeat6: 'Community support',
  pricingFreeCta: 'Get Started Free',

  pricingPayName: 'Pay-per-Use',
  pricingPayPrice: 'from $0.45',
  pricingPayPeriod: 'per 1M tokens',
  pricingPayDesc: 'Lease API access from community',
  pricingPayFeat1: 'Everything in Free',
  pricingPayFeat2: 'No API keys needed',
  pricingPayFeat3: 'Lease from community providers',
  pricingPayFeat4: 'GPT-4o-mini from $0.45/M tokens',
  pricingPayFeat5: 'Claude Haiku from $3/M tokens',
  pricingPayFeat6: 'Perfect for occasional users',
  pricingPayCta: 'Start Using',

  pricingPremName: 'Premium',
  pricingPremPrice: '$29',
  pricingPremPeriod: 'per month',
  pricingPremDesc: 'Managed by Synapsios — best quality',
  pricingPremFeat1: 'Everything in Pay-per-Use',
  pricingPremFeat2: 'Synapsios-managed premium keys',
  pricingPremFeat3: 'Guaranteed uptime & speed',
  pricingPremFeat4: 'Priority access to new models',
  pricingPremFeat5: 'Higher rate limits',
  pricingPremFeat6: 'Priority support',
  pricingPremCta: 'Go Premium',
  pricingMostPopular: 'Most Popular',

  // Landing - Leasing
  leasingTitle: 'API Leasing — How it works',
  leasingIntro: "Don't want to buy a full API subscription? Lease access from other users who share their keys at micro-rates. Perfect for occasional use — pay cents instead of dollars.",
  leasingCommunityTitle: 'Community Providers',
  leasingCommunityDesc: 'Other Synapsios users share their API keys and set their own rates. You pick the provider, price, and model. Cheapest option for light usage.',
  leasingCommunityExample: 'GPT-4 — $0.80 / 1K messages',
  leasingPremiumTitle: 'Synapsios Premium',
  leasingPremiumDesc: 'Our managed keys with guaranteed uptime, fastest speeds, and priority rate limits. Best experience, zero hassle. Flat monthly rate, unlimited use.',
  leasingPremiumExample: 'All models — $29/mo unlimited',
  leasingTip: "Heavy user? Get your own API subscription directly from OpenAI/Anthropic — it's cheaper long-term. Synapsios works perfectly with your own keys at zero extra cost.",

  // Landing - How
  howTitle: 'How it works',
  howStep1Title: 'Create your account',
  howStep1Desc: 'Sign up in seconds. Add your own API keys, or skip and lease access later.',
  howStep2Title: 'Organize your work',
  howStep2Desc: 'Create projects, folders, and threads. Structure your AI conversations the way you think.',
  howStep3Title: 'Chat with any AI',
  howStep3Desc: 'Pick a model, ask your question. Switch models mid-conversation for the best results.',

  // Landing - CTA
  ctaTitle: 'Ready to get started?',
  ctaSubtitle: 'Join Synapsios and discover a new way to work with AI.',
  ctaButton: 'Create your free account',

  // Landing - Footer
  footerTagline: 'Synapsios — Multi-AI Workspace Hub',
  footerTech: 'React + TypeScript + MySQL + Express',

  // Auth
  authTagline: 'Your central workspace for multiple AI models. Organize, chat, analyze — all in one place.',
  authFeat1: 'Multiple AI models in one interface',
  authFeat2: 'Projects, folders, and threads',
  authFeat3: 'Local database, your data stays yours',
  authFeat4: 'Own keys, lease, or go premium',
  authSignIn: 'Sign in',
  authCreateAccount: 'Create account',
  authWelcomeBack: 'Welcome back! Sign in to your account.',
  authCreateNew: 'Create a new account and start working with AI.',
  authDisplayName: 'Display name',
  authEmail: 'Email',
  authPassword: 'Password',
  authMinChars: 'Min. 6 characters',
  authYourPassword: 'Your password',
  authNoAccount: "Don't have an account?",
  authSignUp: 'Sign up',
  authHasAccount: 'Already have an account?',
  authDisplayNameRequired: 'Display name is required',
  authSomethingWrong: 'Something went wrong',
  authNamePlaceholder: 'John Doe',
  authEmailPlaceholder: 'john@example.com',

  // Dashboard
  dashTitle: 'Synapsios',
  dashSubtitle: 'Synapsios — Multi-AI Workspace Hub',
  dashNewProject: 'New Project',
  dashProjectName: 'Project name...',
  dashDescription: 'Description (optional)...',
  dashNoProjects: 'No projects yet. Create your first project to get started.',
  dashRecentActivity: 'Recent Activity',
  dashInThread: ' in ',
  dashNoActivity: 'No activity yet.',

  // Project Explorer
  projSelect: 'Select a project',
  projNewFolder: 'New Folder',
  projNewThread: 'New Thread',
  projDeleteProject: 'Delete project',
  projFolderName: 'Folder name...',
  projThreadTitle: 'Thread title...',
  projEmpty: 'No folders or threads yet. Create one to get started.',

  // Thread View
  threadSelect: 'Select a thread',
  threadLoading: 'Loading thread...',
  threadNotFound: 'Thread not found',
  threadNoMessages: 'No messages yet. Start the conversation below.',
  threadWaiting: 'Waiting for {model} response...',
  threadPlaceholder: 'Message {model}... (Shift+Enter for newline)',
  threadGenerating: 'Generating...',
  threadSummary: 'Summary',
  micStart: 'Start voice input',
  micStop: 'Stop recording',
  micNotSupported: 'Voice input not supported in this browser',

  // Settings
  settingsTitle: 'Settings',
  settingsProviders: 'AI Providers',
  settingsProvidersDesc: 'Configure API keys for AI providers. Keys are stored locally in the MySQL database.',
  settingsKeyConfigured: 'Key configured',
  settingsNoKey: 'No key',
  settingsActive: 'Active',
  settingsInactive: 'Inactive',
  settingsEnterKey: 'Enter API key...',
  settingsModels: 'Models:',
  settingsDefaults: 'Defaults',
  settingsDefaultProvider: 'Default AI Provider',
  settingsAbout: 'About',
  settingsVersion: 'v0.3.0',
  settingsAboutDesc: 'Desktop-first multi-AI workspace hub',
  settingsAboutTech: 'MySQL local storage | React + Vite | Express API',
  settingsLanguage: 'Language',
  settingsLanguageDesc: 'Choose your preferred interface language',

  // Right Panel
  panelDetails: 'Details',
  panelSummary: 'Summary',
  panelFindings: 'Findings',
  panelActivity: 'Activity',
  panelThreadDetails: 'Thread Details',
  panelTitle: 'Title',
  panelProvider: 'Provider',
  panelModel: 'Model',
  panelMessages: 'Messages',
  panelStatus: 'Status',
  panelPriority: 'Priority',
  panelCreated: 'Created',
  panelUpdated: 'Updated',
  panelProjectDetails: 'Project Details',
  panelName: 'Name',
  panelDescription: 'Description',
  panelThreads: 'Threads',
  panelFolders: 'Folders',
  panelSelectHint: 'Select a project or thread to view details',
  panelThreadSummary: 'Thread Summary',
  panelNoSummary: 'No summary generated yet. Open a thread and click "Summary" to generate one.',
  panelKeyFindings: 'Key Findings & Decisions',
  panelNoDecisions: 'No decisions or findings recorded for this project.',
  panelRecentActivity: 'Recent Activity',
  panelNoActivity: 'No recent activity.',

  // Sidebar
  sidebarNewProject: 'New Project',
  sidebarProjectName: 'Project name...',
  sidebarNewFolder: 'New Folder',
  sidebarNewThread: 'New Thread',
  sidebarFolderName: 'Folder name...',
  sidebarThreadTitle: 'Thread title...',

  // Wallet
  walletTitle: 'Wallet',
  walletBalance: 'Balance',
  walletDeposit: 'Deposit',
  walletDepositAmount: 'Amount (USD)',
  walletDepositBtn: 'Add funds',
  walletTotalDeposited: 'Total deposited',
  walletTotalSpent: 'Total spent',
  walletTransactions: 'Transaction History',
  walletNoTransactions: 'No transactions yet.',
  walletTypeDeposit: 'Deposit',
  walletTypeUsage: 'Usage',
  walletTypeLeasingIncome: 'Leasing income',
  walletTypeLeasingCharge: 'Leasing charge',
  walletTypeRefund: 'Refund',

  // Key Management
  keysTitle: 'My API Keys',
  keysDesc: 'Add your own API keys to use AI models directly. Keys are stored securely on the server.',
  keysAdd: 'Add Key',
  keysRemove: 'Remove',
  keysConfigured: 'Configured',
  keysNotSet: 'Not set',
  keysLeaseOut: 'Lease this key',

  // Leasing
  leasingMarketplace: 'API Marketplace',
  leasingMarketplaceDesc: 'Lease API access from other users or offer your own keys.',
  leasingMyListings: 'My Listings',
  leasingAvailable: 'Available Listings',
  leasingCreateListing: 'Create Listing',
  leasingPriceInput: 'Price per 1K input tokens ($)',
  leasingPriceOutput: 'Price per 1K output tokens ($)',
  leasingModel: 'Model',
  leasingProvider: 'Provider',
  leasingOwner: 'Provider',
  leasingRequests: 'Requests',
  leasingEarned: 'Earned',
  leasingActive: 'Active',
  leasingPaused: 'Paused',
  leasingNoListings: 'No listings available.',
  leasingNoMyListings: 'You have no active listings.',
  leasingPerM: '/M tokens',

  // Nav additions
  navWallet: 'Wallet',
  navMarketplace: 'Marketplace',
  navApiKeys: 'API Keys',

  // Theme
  settingsTheme: 'Theme',
  settingsThemeDesc: 'Choose the visual style of the application.',

  // API Keys page
  apiKeysTitle: 'My API Keys',
  apiKeysDesc: 'Manage your private API keys for AI providers. You can add multiple keys per provider.',
  apiKeysAdd: 'Add Key',
  apiKeysProvider: 'Provider',
  apiKeysLabel: 'Label (optional)',
  apiKeysKey: 'API Key',
  apiKeysUsage: 'Usage',
  apiKeysRequests: 'Requests',
  apiKeysTokens: 'Tokens',
  apiKeysNoKeys: 'You have no API keys yet. Add your first key to start using AI models.',
  apiKeysDeleteConfirm: 'Are you sure you want to delete this API key?',

  // Marketplace sharing mode
  leasingSharingMode: 'Sharing mode',
  leasingParallel: 'Parallel work',
  leasingParallelDesc: 'Lessee can use the API even when you are using it',
  leasingExclusive: 'Only when I\'m not using',
  leasingExclusiveDesc: 'Lessee can only use the API when you are not actively using it',

  // Thread creation
  threadSelectProvider: 'Select provider...',
  threadSelectModel: 'Select model...',
  threadSelectLanguage: 'Response language...',
  threadArchive: 'Archive',
  threadArchiveConfirm: 'Archive this thread? It will be moved to the Archive folder.',
  threadDeleteConfirm: 'Delete this thread and all its messages? This cannot be undone.',

  // Project password
  projPassword: 'Password / PIN (optional)',
  projPasswordPlaceholder: 'Leave empty for no protection',
  projLocked: 'Locked',
  projUnlock: 'Unlock',
  projEnterPassword: 'Enter project password',
  projWrongPassword: 'Wrong password',

  // App Builder
  navAppBuilder: 'App Builder',
  appBuilderTitle: 'App Builder',
  appBuilderDesc: 'Create complete applications using AI. Generate code, preview UI, export as ZIP.',
  appBuilderNew: 'New App',
  appBuilderName: 'App name...',
  appBuilderPrompt: 'Describe your app...',
  appBuilderGenerate: 'Generate',
  appBuilderGenerating: 'Generating code...',
  appBuilderPreview: 'Preview',
  appBuilderCode: 'Code',
  appBuilderFiles: 'Files',
  appBuilderErrors: 'Errors',
  appBuilderExport: 'Export ZIP',
  appBuilderNoProjects: 'No apps yet. Create your first app to get started.',
  appBuilderNoFiles: 'No files yet. Use the prompt to generate code.',
  appBuilderNoErrors: 'No errors detected.',
  appBuilderSummary: 'Summary',
  appBuilderDeleteConfirm: 'Delete this app and all its files?',
  appBuilderSaveFile: 'Save',
  appBuilderDeleteFile: 'Delete file',
  appBuilderNewFile: 'New File',
  appBuilderFilePath: 'File path (e.g. src/app.js)...',
  appBuilderResolve: 'Resolve',
  appBuilderClearErrors: 'Clear all',

  // Marketplace extras
  leasingLease: 'Lease',
  leasingYourOffer: 'Your offer',
  leasingDiscount: 'Discount',

  // Wallet extras
  walletTypeWithdrawal: 'Withdrawal',
  walletWithdraw: 'Withdraw',
  walletInvoice: 'Invoice',
  walletBonusInfo: 'Bonus included for larger deposits!',
  walletWithdrawInfo: 'Minimum withdrawal: $10. Funds will be transferred to your payment method.',
  walletWithdrawBtn: 'Withdraw',
  walletAvailable: 'Available',
  walletInvoiceData: 'Invoice Data',
  walletWantInvoice: 'I want a VAT invoice',
  walletCompanyName: 'Company name',
  walletVatNumber: 'EU VAT Number (NIP)',
  walletAddress: 'Address',
  walletCity: 'City',
  walletPostalCode: 'Postal code',
  walletCountry: 'Country',

  // Marketplace v2
  leasingPromotion: 'Promotion',
  leasingLookingForApi: 'Looking for API',
  leasingRequestsDesc: 'Post what API you need. Others can accept or counter-offer.',
  leasingNewRequest: 'New Request',
  leasingNoRequests: 'No requests yet. Be the first to post!',
}

const pl: TranslationKeys = {
  loading: 'Ładowanie...',
  cancel: 'Anuluj',
  create: 'Utwórz',
  save: 'Zapisz',
  delete: 'Usuń',
  send: 'Wyślij',
  dismiss: 'Zamknij',
  you: 'Ty',
  ai: 'AI',
  msgs: 'wiad.',
  messages: 'wiadomości',
  threads: 'wątki',
  folders: 'foldery',
  yes: 'Tak',
  no: 'Nie',
  back: 'Powrót',
  example: 'Przykład',
  vs: 'vs',

  navDashboard: 'Panel główny',
  navSettings: 'Ustawienia',
  navProjects: 'Projekty',
  navSignOut: 'Wyloguj się',
  navHideSidebar: 'Ukryj panel boczny',
  navShowSidebar: 'Pokaż panel boczny',
  navHidePanel: 'Ukryj panel',
  navShowPanel: 'Pokaż panel',

  landingSignIn: 'Zaloguj się',
  landingGetStarted: 'Rozpocznij',
  landingBadge: 'Przestrzeń Multi-AI',
  landingTitle1: 'Każdy model AI.',
  landingTitle2: 'Jedna przestrzeń robocza.',
  landingSubtitle: 'Synapsios to centralny hub do pracy z wieloma modelami AI. Organizuj projekty, prowadź rozmowy z różnymi modelami i nigdy nie trać kontekstu. Użyj własnych kluczy, dzierżaw od innych lub wybierz premium.',
  landingStartFree: 'Zacznij za darmo',
  landingHaveAccount: 'Mam już konto',

  landingNavFeatures: 'Funkcje',
  landingNavPricing: 'Cennik',
  landingNavHow: 'Jak to działa',

  landingMockTitle: 'Synapsios — Przestrzeń AI',
  landingMockProject: 'Mój Projekt',
  landingMockArch: 'Architektura',
  landingMockFrontend: 'Kod frontend',
  landingMockResearch: 'Badania AI',
  landingMockMsg1: 'Jak zaprojektować system auth?',
  landingMockReply1: 'Rekomenduję podejście z tokenami JWT...',
  landingMockMsg2: 'A co na to GPT-4?',
  landingMockReply2: 'Warto rozważyć session-based auth...',

  featuresTitle: 'Wszystko czego potrzebujesz',
  featuresSubtitle: 'Jedna platforma, nieograniczone możliwości',
  featureMultiAiTitle: 'Multi-AI w jednym miejscu',
  featureMultiAiDesc: 'OpenAI, Anthropic, xAI, DeepSeek — każdy ważny model AI dostępny z jednego interfejsu. Przełączaj się między modelami w trakcie rozmowy.',
  featureProjectsTitle: 'Projekty i Foldery',
  featureProjectsDesc: 'Organizuj wątki w projekty i foldery z nieograniczonym zagnieżdżaniem. Każdy projekt to osobna przestrzeń robocza.',
  featureThreadsTitle: 'Inteligentne wątki',
  featureThreadsDesc: 'Każda wiadomość pamięta jaki model ją wygenerował. Pełna historia rozmów z kontekstem wysyłanym do AI automatycznie.',
  featureFastTitle: 'Błyskawicznie szybki',
  featureFastDesc: 'Dedykowana baza danych, zero zewnętrznych zależności. Twoje dane zostają na Twoim serwerze. Bez zimnych startów, bez opóźnień.',
  featureApiTitle: 'Elastyczny dostęp do API',
  featureApiDesc: 'Użyj własnych kluczy API, dzierżaw dostęp od innych użytkowników za małe stawki, lub wykup nasze premium klucze.',
  featureDecisionsTitle: 'Decyzje i Podsumowania',
  featureDecisionsDesc: 'Zapisuj kluczowe ustalenia, generuj podsumowania wątków za pomocą AI. Nigdy nie zgub ważnej decyzji.',

  pricingTitle: 'Prosty, elastyczny cennik',
  pricingSubtitle: 'Użyj własnych kluczy, dzierżaw od społeczności, lub pozwól nam się zająć resztą.\nPłać tylko za to co ma sens.',

  pricingFreeName: 'Darmowy',
  pricingFreePrice: '$0',
  pricingFreePeriod: '',
  pricingFreeDesc: 'Użyj własnych kluczy API',
  pricingFreeFeat1: 'Nieograniczone projekty i wątki',
  pricingFreeFeat2: 'Wszystkie modele AI',
  pricingFreeFeat3: 'Użyj własnych kluczy API',
  pricingFreeFeat4: 'Pełna historia rozmów',
  pricingFreeFeat5: 'Lokalne przechowywanie danych',
  pricingFreeFeat6: 'Wsparcie społeczności',
  pricingFreeCta: 'Zacznij za darmo',

  pricingPayName: 'Pay-per-Use',
  pricingPayPrice: 'od $0.45',
  pricingPayPeriod: 'za 1M tokenów',
  pricingPayDesc: 'Dzierżaw dostęp API od społeczności',
  pricingPayFeat1: 'Wszystko z Darmowego',
  pricingPayFeat2: 'Bez własnych kluczy API',
  pricingPayFeat3: 'Dzierżaw od dostawców społeczności',
  pricingPayFeat4: 'GPT-4o-mini od $0.45/M tokenów',
  pricingPayFeat5: 'Claude Haiku od $3/M tokenów',
  pricingPayFeat6: 'Idealny dla okazjonalnych użytkowników',
  pricingPayCta: 'Zacznij używać',

  pricingPremName: 'Premium',
  pricingPremPrice: '$29',
  pricingPremPeriod: 'miesięcznie',
  pricingPremDesc: 'Zarządzane przez Synapsios — najlepsza jakość',
  pricingPremFeat1: 'Wszystko z Pay-per-Use',
  pricingPremFeat2: 'Klucze premium zarządzane przez Synapsios',
  pricingPremFeat3: 'Gwarantowana dostępność i szybkość',
  pricingPremFeat4: 'Priorytetowy dostęp do nowych modeli',
  pricingPremFeat5: 'Wyższe limity zapytań',
  pricingPremFeat6: 'Wsparcie priorytetowe',
  pricingPremCta: 'Wybierz Premium',
  pricingMostPopular: 'Najpopularniejszy',

  leasingTitle: 'Dzierżawa API — Jak to działa',
  leasingIntro: 'Nie chcesz kupować pełnej subskrypcji API? Dzierżaw dostęp od innych użytkowników, którzy udostępniają swoje klucze za małe stawki. Idealny dla okazjonalnego użytku — płać grosze zamiast dolarów.',
  leasingCommunityTitle: 'Dostawcy społeczności',
  leasingCommunityDesc: 'Inni użytkownicy Synapsios udostępniają swoje klucze API i ustalają własne stawki. Ty wybierasz dostawcę, cenę i model. Najtańsza opcja.',
  leasingCommunityExample: 'GPT-4 — $0.80 / 1K wiadomości',
  leasingPremiumTitle: 'Synapsios Premium',
  leasingPremiumDesc: 'Nasze klucze z gwarantowaną dostępnością, najszybszym działaniem i priorytetowymi limitami. Najlepsze doświadczenie, zero konfiguracji. Stała stawka miesięczna, bez limitów.',
  leasingPremiumExample: 'Wszystkie modele — $29/mc bez limitów',
  leasingTip: 'Przy sporych potrzebach można negocjować warunki lub samemu wykupić wymagane subskrypcje i korzystać z naszego środowiska. Nawet po zakończeniu subskrypcji lub zamknięciu kont AI konwersacje oraz cała baza czatów wraz ze strukturą folderów pozostanie nietknięta.',

  howTitle: 'Jak to działa',
  howStep1Title: 'Stwórz konto',
  howStep1Desc: 'Zarejestruj się w kilka sekund. Dodaj własne klucze API lub pomiń i dzierżaw dostęp później.',
  howStep2Title: 'Organizuj pracę',
  howStep2Desc: 'Twórz projekty, foldery i wątki. Strukturyzuj rozmowy z AI tak jak myślisz.',
  howStep3Title: 'Rozmawiaj z AI',
  howStep3Desc: 'Wybierz model, zadaj pytanie. Przełączaj modele w trakcie rozmowy dla najlepszych rezultatów.',

  ctaTitle: 'Gotowy aby zacząć?',
  ctaSubtitle: 'Dołącz do Synapsios i odkryj nowy sposób pracy z AI.',
  ctaButton: 'Stwórz darmowe konto',

  footerTagline: 'Synapsios — Multi-AI Workspace Hub',
  footerTech: 'React + TypeScript + SQLite + Express',

  authTagline: 'Twoja centralna przestrzeń do pracy z wieloma modelami AI. Organizuj, rozmawiaj, analizuj — wszystko w jednym miejscu.',
  authFeat1: 'Wiele modeli AI w jednym interfejsie',
  authFeat2: 'Projekty, foldery i wątki',
  authFeat3: 'Lokalna baza danych, Twoje dane',
  authFeat4: 'Własne klucze, dzierżawa lub premium',
  authSignIn: 'Zaloguj się',
  authCreateAccount: 'Stwórz konto',
  authWelcomeBack: 'Witaj ponownie! Zaloguj się do swojego konta.',
  authCreateNew: 'Stwórz nowe konto i zacznij pracować z AI.',
  authDisplayName: 'Nazwa użytkownika',
  authEmail: 'Email',
  authPassword: 'Hasło',
  authMinChars: 'Min. 6 znaków',
  authYourPassword: 'Twoje hasło',
  authNoAccount: 'Nie masz konta?',
  authSignUp: 'Zarejestruj się',
  authHasAccount: 'Masz już konto?',
  authDisplayNameRequired: 'Nazwa użytkownika jest wymagana',
  authSomethingWrong: 'Wystąpił błąd',
  authNamePlaceholder: 'Jan Kowalski',
  authEmailPlaceholder: 'jan@example.com',

  dashTitle: 'Synapsios',
  dashSubtitle: 'Synapsios — Multi-AI Workspace Hub',
  dashNewProject: 'Nowy Projekt',
  dashProjectName: 'Nazwa projektu...',
  dashDescription: 'Opis (opcjonalny)...',
  dashNoProjects: 'Brak projektów. Stwórz pierwszy projekt aby rozpocząć.',
  dashRecentActivity: 'Ostatnia aktywność',
  dashInThread: ' w ',
  dashNoActivity: 'Brak aktywności.',

  projSelect: 'Wybierz projekt',
  projNewFolder: 'Nowy Folder',
  projNewThread: 'Nowy Wątek',
  projDeleteProject: 'Usuń projekt',
  projFolderName: 'Nazwa folderu...',
  projThreadTitle: 'Tytuł wątku...',
  projEmpty: 'Brak folderów i wątków. Stwórz pierwszy aby rozpocząć.',

  threadSelect: 'Wybierz wątek',
  threadLoading: 'Ładowanie wątku...',
  threadNotFound: 'Wątek nie znaleziony',
  threadNoMessages: 'Brak wiadomości. Zacznij rozmowę poniżej.',
  threadWaiting: 'Czekam na odpowiedź {model}...',
  threadPlaceholder: 'Wiadomość do {model}... (Shift+Enter nowa linia)',
  threadGenerating: 'Generowanie...',
  threadSummary: 'Podsumowanie',
  micStart: 'Rozpocznij nagrywanie głosu',
  micStop: 'Zatrzymaj nagrywanie',
  micNotSupported: 'Nagrywanie głosu nie jest obsługiwane w tej przeglądarce',

  settingsTitle: 'Ustawienia',
  settingsProviders: 'Dostawcy AI',
  settingsProvidersDesc: 'Konfiguruj klucze API dla dostawców AI. Klucze są przechowywane bezpiecznie na serwerze.',
  settingsKeyConfigured: 'Klucz skonfigurowany',
  settingsNoKey: 'Brak klucza',
  settingsActive: 'Aktywny',
  settingsInactive: 'Nieaktywny',
  settingsEnterKey: 'Wpisz klucz API...',
  settingsModels: 'Modele:',
  settingsDefaults: 'Domyślne',
  settingsDefaultProvider: 'Domyślny dostawca AI',
  settingsAbout: 'O aplikacji',
  settingsVersion: 'v0.3.0',
  settingsAboutDesc: 'Desktop-first multi-AI workspace hub',
  settingsAboutTech: 'SQLite | React + Vite | Express API',
  settingsLanguage: 'Język',
  settingsLanguageDesc: 'Wybierz preferowany język interfejsu',

  panelDetails: 'Szczegóły',
  panelSummary: 'Podsumowanie',
  panelFindings: 'Ustalenia',
  panelActivity: 'Aktywność',
  panelThreadDetails: 'Szczegóły wątku',
  panelTitle: 'Tytuł',
  panelProvider: 'Dostawca',
  panelModel: 'Model',
  panelMessages: 'Wiadomości',
  panelStatus: 'Status',
  panelPriority: 'Priorytet',
  panelCreated: 'Utworzono',
  panelUpdated: 'Zaktualizowano',
  panelProjectDetails: 'Szczegóły projektu',
  panelName: 'Nazwa',
  panelDescription: 'Opis',
  panelThreads: 'Wątki',
  panelFolders: 'Foldery',
  panelSelectHint: 'Wybierz projekt lub wątek aby zobaczyć szczegóły',
  panelThreadSummary: 'Podsumowanie wątku',
  panelNoSummary: 'Brak podsumowania. Otwórz wątek i kliknij "Podsumowanie" aby wygenerować.',
  panelKeyFindings: 'Kluczowe ustalenia i decyzje',
  panelNoDecisions: 'Brak zapisanych decyzji i ustaleń dla tego projektu.',
  panelRecentActivity: 'Ostatnia aktywność',
  panelNoActivity: 'Brak aktywności.',

  sidebarNewProject: 'Nowy Projekt',
  sidebarProjectName: 'Nazwa projektu...',
  sidebarNewFolder: 'Nowy Folder',
  sidebarNewThread: 'Nowy Wątek',
  sidebarFolderName: 'Nazwa folderu...',
  sidebarThreadTitle: 'Tytuł wątku...',

  walletTitle: 'Portfel',
  walletBalance: 'Saldo',
  walletDeposit: 'Wpłata',
  walletDepositAmount: 'Kwota (USD)',
  walletDepositBtn: 'Dodaj środki',
  walletTotalDeposited: 'Łącznie wpłacono',
  walletTotalSpent: 'Łącznie wydano',
  walletTransactions: 'Historia transakcji',
  walletNoTransactions: 'Brak transakcji.',
  walletTypeDeposit: 'Wpłata',
  walletTypeUsage: 'Użycie',
  walletTypeLeasingIncome: 'Przychód z dzierżawy',
  walletTypeLeasingCharge: 'Opłata za dzierżawę',
  walletTypeRefund: 'Zwrot',

  keysTitle: 'Moje klucze API',
  keysDesc: 'Dodaj własne klucze API aby używać modeli AI bezpośrednio. Klucze są bezpiecznie przechowywane na serwerze.',
  keysAdd: 'Dodaj klucz',
  keysRemove: 'Usuń',
  keysConfigured: 'Skonfigurowany',
  keysNotSet: 'Nie ustawiony',
  keysLeaseOut: 'Dzierżaw ten klucz',

  leasingMarketplace: 'Rynek API',
  leasingMarketplaceDesc: 'Dzierżaw dostęp API od innych użytkowników lub oferuj własne klucze.',
  leasingMyListings: 'Moje oferty',
  leasingAvailable: 'Dostępne oferty',
  leasingCreateListing: 'Stwórz ofertę',
  leasingPriceInput: 'Cena za 1K tokenów wejścia ($)',
  leasingPriceOutput: 'Cena za 1K tokenów wyjścia ($)',
  leasingModel: 'Model',
  leasingProvider: 'Dostawca',
  leasingOwner: 'Dostawca',
  leasingRequests: 'Zapytania',
  leasingEarned: 'Zarobiono',
  leasingActive: 'Aktywna',
  leasingPaused: 'Wstrzymana',
  leasingNoListings: 'Brak dostępnych ofert.',
  leasingNoMyListings: 'Nie masz aktywnych ofert.',
  leasingPerM: '/M tokenów',

  navWallet: 'Portfel',
  navMarketplace: 'Rynek',
  navApiKeys: 'Klucze API',

  settingsTheme: 'Motyw',
  settingsThemeDesc: 'Wybierz styl wizualny aplikacji.',

  apiKeysTitle: 'Moje klucze API',
  apiKeysDesc: 'Zarządzaj swoimi prywatnymi kluczami API dla dostawców AI. Możesz dodać wiele kluczy na dostawcę.',
  apiKeysAdd: 'Dodaj klucz',
  apiKeysProvider: 'Dostawca',
  apiKeysLabel: 'Etykieta (opcjonalnie)',
  apiKeysKey: 'Klucz API',
  apiKeysUsage: 'Zużycie',
  apiKeysRequests: 'Zapytania',
  apiKeysTokens: 'Tokeny',
  apiKeysNoKeys: 'Nie masz jeszcze żadnych kluczy API. Dodaj swój pierwszy klucz, aby zacząć korzystać z modeli AI.',
  apiKeysDeleteConfirm: 'Czy na pewno chcesz usunąć ten klucz API?',

  leasingSharingMode: 'Tryb udostępniania',
  leasingParallel: 'Praca równoległa',
  leasingParallelDesc: 'Dzierżawca może korzystać z API nawet gdy sam go używasz',
  leasingExclusive: 'Tylko gdy nie używam',
  leasingExclusiveDesc: 'Dzierżawca może korzystać z API tylko gdy sam go nie używasz',

  // Thread creation
  threadSelectProvider: 'Wybierz dostawcę...',
  threadSelectModel: 'Wybierz model...',
  threadSelectLanguage: 'Język odpowiedzi...',
  threadArchive: 'Archiwizuj',
  threadArchiveConfirm: 'Archiwizować ten wątek? Zostanie przeniesiony do folderu Archiwum.',
  threadDeleteConfirm: 'Usunąć ten wątek i wszystkie jego wiadomości? Tej operacji nie można cofnąć.',

  // Project password
  projPassword: 'Hasło / PIN (opcjonalnie)',
  projPasswordPlaceholder: 'Zostaw puste aby nie chronić',
  projLocked: 'Zablokowany',
  projUnlock: 'Odblokuj',
  projEnterPassword: 'Wprowadź hasło projektu',
  projWrongPassword: 'Błędne hasło',

  navAppBuilder: 'Kreator Aplikacji',
  appBuilderTitle: 'Kreator Aplikacji',
  appBuilderDesc: 'Twórz kompletne aplikacje za pomocą AI. Generuj kod, podglądaj UI, eksportuj jako ZIP.',
  appBuilderNew: 'Nowa Aplikacja',
  appBuilderName: 'Nazwa aplikacji...',
  appBuilderPrompt: 'Opisz swoją aplikację...',
  appBuilderGenerate: 'Generuj',
  appBuilderGenerating: 'Generowanie kodu...',
  appBuilderPreview: 'Podgląd',
  appBuilderCode: 'Kod',
  appBuilderFiles: 'Pliki',
  appBuilderErrors: 'Błędy',
  appBuilderExport: 'Eksport ZIP',
  appBuilderNoProjects: 'Brak aplikacji. Stwórz pierwszą aby rozpocząć.',
  appBuilderNoFiles: 'Brak plików. Użyj promptu aby wygenerować kod.',
  appBuilderNoErrors: 'Brak wykrytych błędów.',
  appBuilderSummary: 'Podsumowanie',
  appBuilderDeleteConfirm: 'Usunąć tę aplikację i wszystkie jej pliki?',
  appBuilderSaveFile: 'Zapisz',
  appBuilderDeleteFile: 'Usuń plik',
  appBuilderNewFile: 'Nowy Plik',
  appBuilderFilePath: 'Ścieżka pliku (np. src/app.js)...',
  appBuilderResolve: 'Rozwiąż',
  appBuilderClearErrors: 'Wyczyść wszystko',

  leasingLease: 'Dzierżaw',
  leasingYourOffer: 'Twoja oferta',
  leasingDiscount: 'Rabat',

  walletTypeWithdrawal: 'Wypłata',
  walletWithdraw: 'Wypłać',
  walletInvoice: 'Faktura',
  walletBonusInfo: 'Bonus za większe wpłaty!',
  walletWithdrawInfo: 'Minimalna wypłata: $10. Środki zostaną przelane na Twoją metodę płatności.',
  walletWithdrawBtn: 'Wypłać',
  walletAvailable: 'Dostępne',
  walletInvoiceData: 'Dane do faktury',
  walletWantInvoice: 'Chcę fakturę VAT',
  walletCompanyName: 'Nazwa firmy',
  walletVatNumber: 'NIP europejski',
  walletAddress: 'Adres',
  walletCity: 'Miasto',
  walletPostalCode: 'Kod pocztowy',
  walletCountry: 'Kraj',

  leasingPromotion: 'Promocja',
  leasingLookingForApi: 'Szukam API',
  leasingRequestsDesc: 'Dodaj zapytanie o API. Inni mogą je zaakceptować lub złożyć kontrofertę.',
  leasingNewRequest: 'Nowe zapytanie',
  leasingNoRequests: 'Brak zapytań. Bądź pierwszy!',
}

const sk: TranslationKeys = {
  loading: 'Nacitavam...',
  cancel: 'Zrusit',
  create: 'Vytvorit',
  save: 'Ulozit',
  delete: 'Vymazat',
  send: 'Odoslat',
  dismiss: 'Zavriet',
  you: 'Ty',
  ai: 'AI',
  msgs: 'spr.',
  messages: 'spravy',
  threads: 'vlakna',
  folders: 'priecinky',
  yes: 'Ano',
  no: 'Nie',
  back: 'Spat',
  example: 'Priklad',
  vs: 'vs',

  navDashboard: 'Prehlad',
  navSettings: 'Nastavenia',
  navProjects: 'Projekty',
  navSignOut: 'Odhlasit sa',
  navHideSidebar: 'Skryt bocny panel',
  navShowSidebar: 'Zobrazit bocny panel',
  navHidePanel: 'Skryt panel',
  navShowPanel: 'Zobrazit panel',

  landingSignIn: 'Prihlasit sa',
  landingGetStarted: 'Zacat',
  landingBadge: 'Multi-AI Pracovny priestor',
  landingTitle1: 'Kazdy AI model.',
  landingTitle2: 'Jeden pracovny priestor pre vsetko.',
  landingSubtitle: 'Synapsios je centralny hub pre pracu s viacerymi AI modelmi. Organizujte projekty, komunikujte s roznymi modelmi a nikdy nestrate kontext. Pouzite vlastne kluce, prenajmite od inych alebo si zvolte premium.',
  landingStartFree: 'Zacat zadarmo',
  landingHaveAccount: 'Mam uz ucet',

  landingNavFeatures: 'Funkcie',
  landingNavPricing: 'Cennik',
  landingNavHow: 'Ako to funguje',

  landingMockTitle: 'Synapsios — AI Priestor',
  landingMockProject: 'Moj Projekt',
  landingMockArch: 'Architektura',
  landingMockFrontend: 'Frontend Kod',
  landingMockResearch: 'AI Vyskum',
  landingMockMsg1: 'Ako navrnut system autentifikacie?',
  landingMockReply1: 'Odporucam pristup s tokenmi JWT...',
  landingMockMsg2: 'Co na to GPT-4?',
  landingMockReply2: 'Zvazite autentifikaciu na zaklade sedeni...',

  featuresTitle: 'Vsetko co potrebujete',
  featuresSubtitle: 'Jedna platforma, neobmedzene moznosti',
  featureMultiAiTitle: 'Multi-AI na jednom mieste',
  featureMultiAiDesc: 'OpenAI, Anthropic, xAI, DeepSeek — kazdy dolezity AI model pristupny z jedneho rozhrania. Prepinajte medzi modelmi pocas konverzacie.',
  featureProjectsTitle: 'Projekty a Priecinky',
  featureProjectsDesc: 'Organizujte vlakna do projektov a priecinkov s neobmedzenym vnorenim. Kazdy projekt je vlastny pracovny priestor.',
  featureThreadsTitle: 'Inteligentne vlakna',
  featureThreadsDesc: 'Kazda sprava si pamatuje, ktory model ju vygeneroval. Uplna historia konverzacii s kontextom odoslanym AI automaticky.',
  featureFastTitle: 'Bleskovo rychly',
  featureFastDesc: 'Lokalna databaza MySQL, ziadne externe zavislosti. Vase data zostavaju na vasom serveri. Ziadne studene starty, ziadna latencia.',
  featureApiTitle: 'Flexibilny pristup k API',
  featureApiDesc: 'Pouzite vlastne API kluce, prenajmite pristup od inych pouzivatelov za mikro-sadzby, alebo si predplatte nase premium kluce.',
  featureDecisionsTitle: 'Rozhodnutia a Zhrnutia',
  featureDecisionsDesc: 'Ukladajte klucove zistenia, generujte zhrnutia vlakien pomocou AI. Uz nikdy nestratite dolezite rozhodnutie.',

  pricingTitle: 'Jednoduchy, flexibilny cennik',
  pricingSubtitle: 'Pouzite vlastne kluce, prenajmite od komunity, alebo nechajte vsetko na nas.\nPlatte len za to, co davava zmysel.',

  pricingFreeName: 'Zadarmo',
  pricingFreePrice: '$0',
  pricingFreePeriod: '',
  pricingFreeDesc: 'Pouzite vlastne API kluce',
  pricingFreeFeat1: 'Neobmedzene projekty a vlakna',
  pricingFreeFeat2: 'Vsetky AI modely podporovane',
  pricingFreeFeat3: 'Pouzite vlastne API kluce',
  pricingFreeFeat4: 'Uplna historia konverzacii',
  pricingFreeFeat5: 'Lokalne ukladanie dat',
  pricingFreeFeat6: 'Podpora komunity',
  pricingFreeCta: 'Zacat zadarmo',

  pricingPayName: 'Pay-per-Use',
  pricingPayPrice: 'od $0.45',
  pricingPayPeriod: 'za 1M tokenov',
  pricingPayDesc: 'Prenajmite API pristup od komunity',
  pricingPayFeat1: 'Vsetko zo Zadarmo',
  pricingPayFeat2: 'Ziadne vlastne API kluce',
  pricingPayFeat3: 'Prenajmite od komunitnych poskytovatelov',
  pricingPayFeat4: 'GPT-4o-mini od $0.45/M tokenov',
  pricingPayFeat5: 'Claude Haiku od $3/M tokenov',
  pricingPayFeat6: 'Idealny pre prilezitostnych pouzivatelov',
  pricingPayCta: 'Zacat pouzivat',

  pricingPremName: 'Premium',
  pricingPremPrice: '$29',
  pricingPremPeriod: 'mesacne',
  pricingPremDesc: 'Spravovane Synapsios — najlepsia kvalita',
  pricingPremFeat1: 'Vsetko z Pay-per-Use',
  pricingPremFeat2: 'Synapsios premium kluce',
  pricingPremFeat3: 'Garantovana dostupnost a rychlost',
  pricingPremFeat4: 'Prioritny pristup k novym modelom',
  pricingPremFeat5: 'Vyssie limity poziadaviek',
  pricingPremFeat6: 'Prioritna podpora',
  pricingPremCta: 'Zvolit Premium',
  pricingMostPopular: 'Najpopularnejsi',

  leasingTitle: 'Prenajom API — Ako to funguje',
  leasingIntro: 'Nechcete kupovat plnu API predplatnu? Prenajmite pristup od inych pouzivatelov, ktori zdielaju svoje kluce za mikro-sadzby. Idealny pre prilezitostne pouzitie — platte centy namiesto dolarov.',
  leasingCommunityTitle: 'Komunitni poskytovatelia',
  leasingCommunityDesc: 'Ini pouzivatelia Synapsios zdielaju svoje API kluce a nastavuju vlastne sadzby. Vy si vyberete poskytovatela, cenu a model. Najlacnejsia moznost.',
  leasingCommunityExample: 'GPT-4 — $0.80 / 1K sprav',
  leasingPremiumTitle: 'Synapsios Premium',
  leasingPremiumDesc: 'Nase kluce s garantovanou dostupnostou, najrychlejsim vykonom a prioritnymi limitmi. Najlepsi zazitok, ziadna konfiguracia. Pausalna mesacna sadzba, bez limitov.',
  leasingPremiumExample: 'Vsetky modely — $29/mes bez limitov',
  leasingTip: 'Velky pouzivatel? Kupte si vlastnu predplatnu priamo od OpenAI/Anthropic — dlhodobo lacnejsie. Synapsios funguje dokonale s vlastnymi klucmi bez dalsich nakladov.',

  howTitle: 'Ako to funguje',
  howStep1Title: 'Vytvorte si ucet',
  howStep1Desc: 'Zaregistrujte sa za par sekund. Pridajte vlastne API kluce, alebo preskocte a prenajmite pristup neskor.',
  howStep2Title: 'Organizujte pracu',
  howStep2Desc: 'Vytvarajte projekty, priecinky a vlakna. Strukturujte konverzacie s AI tak ako premyslate.',
  howStep3Title: 'Komunikujte s AI',
  howStep3Desc: 'Vyberte model, polozite otazku. Prepinajte modely pocas konverzacie pre najlepsie vysledky.',

  ctaTitle: 'Pripraveni zacat?',
  ctaSubtitle: 'Pridajte sa k Synapsios a objavte novy sposob prace s AI.',
  ctaButton: 'Vytvorit bezplatny ucet',

  footerTagline: 'Synapsios — Multi-AI Workspace Hub',
  footerTech: 'React + TypeScript + MySQL + Express',

  authTagline: 'Vas centralny pracovny priestor pre viacero AI modelov. Organizujte, komunikujte, analyzujte — vsetko na jednom mieste.',
  authFeat1: 'Viacero AI modelov v jednom rozhrani',
  authFeat2: 'Projekty, priecinky a vlakna',
  authFeat3: 'Lokalna databaza, vase data zostavaju vase',
  authFeat4: 'Vlastne kluce, prenajom alebo premium',
  authSignIn: 'Prihlasit sa',
  authCreateAccount: 'Vytvorit ucet',
  authWelcomeBack: 'Vitajte spat! Prihlaste sa do svojho uctu.',
  authCreateNew: 'Vytvorte si novy ucet a zacnite pracovat s AI.',
  authDisplayName: 'Zobrazovane meno',
  authEmail: 'Email',
  authPassword: 'Heslo',
  authMinChars: 'Min. 6 znakov',
  authYourPassword: 'Vase heslo',
  authNoAccount: 'Nemate ucet?',
  authSignUp: 'Zaregistrovat sa',
  authHasAccount: 'Uz mate ucet?',
  authDisplayNameRequired: 'Zobrazovane meno je povinne',
  authSomethingWrong: 'Nieco sa pokazilo',
  authNamePlaceholder: 'Jan Novak',
  authEmailPlaceholder: 'jan@example.com',

  dashTitle: 'Synapsios',
  dashSubtitle: 'Synapsios — Multi-AI Workspace Hub',
  dashNewProject: 'Novy Projekt',
  dashProjectName: 'Nazov projektu...',
  dashDescription: 'Popis (volitelny)...',
  dashNoProjects: 'Ziadne projekty. Vytvorte prvy projekt na zacatie.',
  dashRecentActivity: 'Nedavna aktivita',
  dashInThread: ' v ',
  dashNoActivity: 'Ziadna aktivita.',

  projSelect: 'Vyberte projekt',
  projNewFolder: 'Novy Priecinok',
  projNewThread: 'Nove Vlakno',
  projDeleteProject: 'Vymazat projekt',
  projFolderName: 'Nazov priecinka...',
  projThreadTitle: 'Nazov vlakna...',
  projEmpty: 'Ziadne priecinky ani vlakna. Vytvorte prvy na zacatie.',

  threadSelect: 'Vyberte vlakno',
  threadLoading: 'Nacitavam vlakno...',
  threadNotFound: 'Vlakno nenajdene',
  threadNoMessages: 'Ziadne spravy. Zacnite konverzaciu nizsie.',
  threadWaiting: 'Cakam na odpoved {model}...',
  threadPlaceholder: 'Sprava pre {model}... (Shift+Enter novy riadok)',
  threadGenerating: 'Generujem...',
  threadSummary: 'Zhrnutie',
  micStart: 'Spustit hlasovy vstup',
  micStop: 'Zastavit nahravanie',
  micNotSupported: 'Hlasovy vstup nie je podporovany v tomto prehliadaci',

  settingsTitle: 'Nastavenia',
  settingsProviders: 'AI Poskytovatelia',
  settingsProvidersDesc: 'Konfigurujte API kluce pre AI poskytovatelov. Kluce su ukladane lokalne v MySQL databaze.',
  settingsKeyConfigured: 'Kluc nakonfigurovany',
  settingsNoKey: 'Ziadny kluc',
  settingsActive: 'Aktivny',
  settingsInactive: 'Neaktivny',
  settingsEnterKey: 'Zadajte API kluc...',
  settingsModels: 'Modely:',
  settingsDefaults: 'Predvolene',
  settingsDefaultProvider: 'Predvoleny AI poskytovatel',
  settingsAbout: 'O aplikacii',
  settingsVersion: 'v0.3.0',
  settingsAboutDesc: 'Desktop-first multi-AI workspace hub',
  settingsAboutTech: 'MySQL lokalne | React + Vite | Express API',
  settingsLanguage: 'Jazyk',
  settingsLanguageDesc: 'Zvolte preferovany jazyk rozhrania',

  panelDetails: 'Podrobnosti',
  panelSummary: 'Zhrnutie',
  panelFindings: 'Zistenia',
  panelActivity: 'Aktivita',
  panelThreadDetails: 'Podrobnosti vlakna',
  panelTitle: 'Nazov',
  panelProvider: 'Poskytovatel',
  panelModel: 'Model',
  panelMessages: 'Spravy',
  panelStatus: 'Stav',
  panelPriority: 'Priorita',
  panelCreated: 'Vytvorene',
  panelUpdated: 'Aktualizovane',
  panelProjectDetails: 'Podrobnosti projektu',
  panelName: 'Nazov',
  panelDescription: 'Popis',
  panelThreads: 'Vlakna',
  panelFolders: 'Priecinky',
  panelSelectHint: 'Vyberte projekt alebo vlakno pre zobrazenie podrobnosti',
  panelThreadSummary: 'Zhrnutie vlakna',
  panelNoSummary: 'Ziadne zhrnutie. Otvorte vlakno a kliknite "Zhrnutie" pre vygenerovanie.',
  panelKeyFindings: 'Klucove zistenia a rozhodnutia',
  panelNoDecisions: 'Ziadne zaznamenane rozhodnutia a zistenia pre tento projekt.',
  panelRecentActivity: 'Nedavna aktivita',
  panelNoActivity: 'Ziadna nedavna aktivita.',

  sidebarNewProject: 'Novy Projekt',
  sidebarProjectName: 'Nazov projektu...',
  sidebarNewFolder: 'Novy Priecinok',
  sidebarNewThread: 'Nove Vlakno',
  sidebarFolderName: 'Nazov priecinka...',
  sidebarThreadTitle: 'Nazov vlakna...',

  walletTitle: 'Penazenka',
  walletBalance: 'Zostatok',
  walletDeposit: 'Vklad',
  walletDepositAmount: 'Suma (USD)',
  walletDepositBtn: 'Pridat prostriedky',
  walletTotalDeposited: 'Celkovo vlozene',
  walletTotalSpent: 'Celkovo minutee',
  walletTransactions: 'Historia transakcii',
  walletNoTransactions: 'Ziadne transakcie.',
  walletTypeDeposit: 'Vklad',
  walletTypeUsage: 'Pouzitie',
  walletTypeLeasingIncome: 'Prijem z prenajmu',
  walletTypeLeasingCharge: 'Poplatok za prenajom',
  walletTypeRefund: 'Vratenie',

  keysTitle: 'Moje API kluce',
  keysDesc: 'Pridajte vlastne API kluce pre priame pouzitie AI modelov. Kluce su bezpecne ulozene na serveri.',
  keysAdd: 'Pridat kluc',
  keysRemove: 'Odstranit',
  keysConfigured: 'Nakonfigurovany',
  keysNotSet: 'Nenastaveny',
  keysLeaseOut: 'Prenajat tento kluc',

  leasingMarketplace: 'API Trhovisko',
  leasingMarketplaceDesc: 'Prenajmite API pristup od inych pouzivatelov alebo ponuknite vlastne kluce.',
  leasingMyListings: 'Moje ponuky',
  leasingAvailable: 'Dostupne ponuky',
  leasingCreateListing: 'Vytvorit ponuku',
  leasingPriceInput: 'Cena za 1K vstupnych tokenov ($)',
  leasingPriceOutput: 'Cena za 1K vystupnych tokenov ($)',
  leasingModel: 'Model',
  leasingProvider: 'Poskytovatel',
  leasingOwner: 'Poskytovatel',
  leasingRequests: 'Poziadavky',
  leasingEarned: 'Zarobene',
  leasingActive: 'Aktivna',
  leasingPaused: 'Pozastavena',
  leasingNoListings: 'Ziadne dostupne ponuky.',
  leasingNoMyListings: 'Nemate ziadne aktivne ponuky.',
  leasingPerM: '/M tokenov',

  navWallet: 'Penazenka',
  navMarketplace: 'Trhovisko',
  navApiKeys: 'API Kluce',

  settingsTheme: 'Tema',
  settingsThemeDesc: 'Vyberte vizualny styl aplikacie.',

  apiKeysTitle: 'Moje API kluce',
  apiKeysDesc: 'Spravujte svoje sukromne API kluce pre AI poskytovatelov. Mozete pridat viacero klucov na poskytovatela.',
  apiKeysAdd: 'Pridat kluc',
  apiKeysProvider: 'Poskytovatel',
  apiKeysLabel: 'Oznacenie (volitelne)',
  apiKeysKey: 'API kluc',
  apiKeysUsage: 'Pouzitie',
  apiKeysRequests: 'Poziadavky',
  apiKeysTokens: 'Tokeny',
  apiKeysNoKeys: 'Nemate ziadne API kluce. Pridajte prvy kluc na pouzivanie AI modelov.',
  apiKeysDeleteConfirm: 'Naozaj chcete vymazat tento API kluc?',

  leasingSharingMode: 'Rezim zdielania',
  leasingParallel: 'Rovnobezna praca',
  leasingParallelDesc: 'Najomnik moze pouzivat API aj ked ho sami pouzivate',
  leasingExclusive: 'Len ked nepouzivam',
  leasingExclusiveDesc: 'Najomnik moze pouzivat API len ked ho sami nepouzivate',

  // Thread creation
  threadSelectProvider: 'Vyberte poskytovatela...',
  threadSelectModel: 'Vyberte model...',
  threadSelectLanguage: 'Jazyk odpovede...',
  threadArchive: 'Archivovat',
  threadArchiveConfirm: 'Archivovat toto vlakno? Bude presunuty do priecinku Archiv.',
  threadDeleteConfirm: 'Vymazat toto vlakno a vsetky jeho spravy? Tuto operaciu nie je mozne vratit.',

  // Project password
  projPassword: 'Heslo / PIN (volitelne)',
  projPasswordPlaceholder: 'Nechajte prazdne pre ziadnu ochranu',
  projLocked: 'Zamknuty',
  projUnlock: 'Odomknut',
  projEnterPassword: 'Zadajte heslo projektu',
  projWrongPassword: 'Nespravne heslo',

  navAppBuilder: 'Tvorba Aplikacii',
  appBuilderTitle: 'Tvorba Aplikacii',
  appBuilderDesc: 'Vytvarajte kompletne aplikacie pomocou AI. Generujte kod, nahlad UI, export ako ZIP.',
  appBuilderNew: 'Nova Aplikacia',
  appBuilderName: 'Nazov aplikacie...',
  appBuilderPrompt: 'Popiste svoju aplikaciu...',
  appBuilderGenerate: 'Generovat',
  appBuilderGenerating: 'Generujem kod...',
  appBuilderPreview: 'Nahlad',
  appBuilderCode: 'Kod',
  appBuilderFiles: 'Subory',
  appBuilderErrors: 'Chyby',
  appBuilderExport: 'Export ZIP',
  appBuilderNoProjects: 'Ziadne aplikacie. Vytvorte prvu na zacatie.',
  appBuilderNoFiles: 'Ziadne subory. Pouzite prompt na vygenerovanie kodu.',
  appBuilderNoErrors: 'Ziadne detekovane chyby.',
  appBuilderSummary: 'Zhrnutie',
  appBuilderDeleteConfirm: 'Vymazat tuto aplikaciu a vsetky jej subory?',
  appBuilderSaveFile: 'Ulozit',
  appBuilderDeleteFile: 'Vymazat subor',
  appBuilderNewFile: 'Novy Subor',
  appBuilderFilePath: 'Cesta k suboru (napr. src/app.js)...',
  appBuilderResolve: 'Vyriesit',
  appBuilderClearErrors: 'Vycistit vsetko',

  leasingLease: 'Prenajat',
  leasingYourOffer: 'Vasa ponuka',
  leasingDiscount: 'Zlava',

  walletTypeWithdrawal: 'Vyber',
  walletWithdraw: 'Vybrat',
  walletInvoice: 'Faktura',
  walletBonusInfo: 'Bonus za vacsie vklady!',
  walletWithdrawInfo: 'Minimalny vyber: $10. Prostriedky budu prevedene na vasu platobnu metodu.',
  walletWithdrawBtn: 'Vybrat',
  walletAvailable: 'Dostupne',
  walletInvoiceData: 'Fakturacne udaje',
  walletWantInvoice: 'Chcem fakturu s DPH',
  walletCompanyName: 'Nazov firmy',
  walletVatNumber: 'IC DPH',
  walletAddress: 'Adresa',
  walletCity: 'Mesto',
  walletPostalCode: 'PSC',
  walletCountry: 'Krajina',

  leasingPromotion: 'Promockia',
  leasingLookingForApi: 'Hladam API',
  leasingRequestsDesc: 'Pridajte poziadavku na API. Ostatni mozu akceptovat alebo kontroponukat.',
  leasingNewRequest: 'Nova poziadavka',
  leasingNoRequests: 'Ziadne poziadavky. Bud prvy!',
}

export const translations: Record<Language, TranslationKeys> = { en, pl, sk }
